import React, { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { Breadcrumbs, Btn, H5 } from '../../AbstractElements';
import {
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Label,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
} from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import ConfirmationModal from '../UiKits/ConfirmationModal';
import AdminChangePlanModal from './AdminChangePlanModal';
import { useForm } from 'react-hook-form';
import { Username } from '../../Constant';
import {
  useGetUsersQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useAddUserNoteMutation,
  useMarkUserNoteCompleteMutation,
  useEditUserNoteMutation,
  useGetUserSubscriptionsQuery,
  useCancelUserSubscriptionMutation,
  usePauseUserSubscriptionMutation,
  useResumeUserSubscriptionMutation,
} from '../../features/users/userApiSlice';

const formatPendingChangeDate = (isoString) => {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const isEligiblePendingPlanChange = (sub) => {
  if (String(sub?.status || '').toLowerCase() !== 'active') return false;
  const pending = sub?.pendingPlanChange;
  if (!pending?.effectiveAt) return false;
  if (pending.fromLabel && pending.toLabel && pending.fromLabel === pending.toLabel) return false;
  return true;
};

/** One active row per Stripe subscription — highest id wins (current row). */
const buildPendingUpgradeOwnerIds = (subscriptions) => {
  const ownerByStripeId = new Map();
  for (const sub of subscriptions) {
    if (!isEligiblePendingPlanChange(sub)) continue;
    const stripeId = sub.payment_subId;
    const key = stripeId || `__sub_${sub.id}`;
    const existing = ownerByStripeId.get(key);
    if (!existing || sub.id > existing.id) {
      ownerByStripeId.set(key, sub);
    }
  }
  return new Set([...ownerByStripeId.values()].map((sub) => sub.id));
};

const hasPendingUpgrade = (sub, pendingUpgradeOwnerIds) =>
  pendingUpgradeOwnerIds.has(sub.id);

const formatPendingChangeSummary = (sub, pendingChangeDate) => {
  if (!pendingChangeDate) return null;
  const { fromLabel, toLabel, targetAmount, toCadenceMonths, savingsPercent } = sub.pendingPlanChange || {};
  const cadence = toCadenceMonths > 0 ? toCadenceMonths : 1;
  const amount = Number(targetAmount);
  const savings = Number(savingsPercent);

  if (fromLabel && toLabel && Number.isFinite(amount) && Number.isFinite(savings) && savings > 0) {
    return `Plan change scheduled: ${fromLabel} → ${toLabel} on ${pendingChangeDate} (€${amount.toFixed(2)} every ${cadence} month${cadence === 1 ? '' : 's'}, save ${Math.round(savings)}%)`;
  }
  if (fromLabel && toLabel && Number.isFinite(amount)) {
    return `Plan change scheduled: ${fromLabel} → ${toLabel} on ${pendingChangeDate} (€${amount.toFixed(2)} every ${cadence} month${cadence === 1 ? '' : 's'})`;
  }
  if (fromLabel && toLabel) {
    return `Plan change scheduled: ${fromLabel} → ${toLabel} on ${pendingChangeDate}`;
  }
  return `Scheduled plan change on ${pendingChangeDate}`;
};

const Users = () => {
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState([]);
  const [activeChecked, setActiveChecked] = useState(false);
  const [passwordChecked, setPasswordChecked] = useState(false);
  const [toggleSelect, setToggleSelect] = useState(false);
  const [editing, setEditing] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [markingCompleteId, setMarkingCompleteId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Pagination and search states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalRows, setTotalRows] = useState(0);

  const [addUser] = useAddUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [addUserNote] = useAddUserNoteMutation();
  const [markUserNoteComplete] = useMarkUserNoteCompleteMutation();
  const [editUserNote] = useEditUserNoteMutation();
  const [cancelUserSubscription] = useCancelUserSubscriptionMutation();
  const [pauseUserSubscription] = usePauseUserSubscriptionMutation();
  const [resumeUserSubscription] = useResumeUserSubscriptionMutation();
  const {
    data: subscriptionsResponse,
    isFetching: subsFetching,
    refetch: refetchSubs,
  } = useGetUserSubscriptionsQuery(selectedUser?.id, { skip: !selectedUser?.id });
  const subscriptions = subscriptionsResponse?.data || [];
  const pendingUpgradeOwnerIds = useMemo(
    () => buildPendingUpgradeOwnerIds(subscriptions),
    [subscriptions],
  );
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelNote, setCancelNote] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [pauseTarget, setPauseTarget] = useState(null);
  const [pauseMode, setPauseMode] = useState('SKIP_1'); // SKIP_1 | SKIP_2 | UNTIL_DATE
  const [pauseUntilDate, setPauseUntilDate] = useState('');
  const [pauseNote, setPauseNote] = useState('');
  const [isPausing, setIsPausing] = useState(false);
  const [resumeTarget, setResumeTarget] = useState(null);
  const [resumeMode, setResumeMode] = useState('IMMEDIATE'); // IMMEDIATE | ON_DATE
  const [resumeOnDate, setResumeOnDate] = useState('');
  const [isResuming, setIsResuming] = useState(false);
  const [changePlanTarget, setChangePlanTarget] = useState(null);

  const canAdminChangePlan = (sub) => {
    if (hasPendingUpgrade(sub, pendingUpgradeOwnerIds)) return false;
    if (sub.status !== 'active' || !sub.payment_subId) return false;
    const durationNum = parseInt(String(sub.duration ?? ''), 10);
    if ([30, 90].includes(durationNum)) return false;
    const productType = String(sub.product_type || '').toLowerCase();
    if (productType === 'riise_formula') return [3, 6].includes(durationNum);
    if (productType === 'omega-3') return [1, 3].includes(durationNum);
    return false;
  };

  const {
    data: fetchUsers,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetUsersQuery({
    email: localStorage.getItem('email'),
    page,
    limit,
    search: debouncedSearch,
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fn = () => {
      if (isSuccess) {
        console.log('fetchUsers => ', fetchUsers.data);
        setData(fetchUsers.data?.rows || []);
        setTotalRows(fetchUsers.data?.count || 0);
      }
    };
    fn();
  }, [fetchUsers, isSuccess]);

  const handleRowSelected = useCallback((state) => {
    console.log(state);
    setSelectedRow(state.selectedRows);
    if (state.selectedCount === 0) {
      clears();
    }
  }, []);

  const clears = () => {
    setEditing(false);
    setValue('name', '');
    setValue('userName', '');
    setValue('password', '');
    setActiveChecked(false);
  };

  const handleEdit = () => {
    console.log(selectedRow[0]);
    setEditing(true);
    setValue('name', selectedRow[0].name);
    setValue('userName', selectedRow[0].email);
    setActiveChecked(selectedRow[0].active === 'Yes');
  };

  const [modalOpen, setModalOpen] = useState(false);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleConfirmAction = () => {
    handleDelete();
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowNoteInput(false);
    setNoteText('');
    setViewModalOpen(true);
  };

  const toggleViewModal = () => {
    setViewModalOpen(!viewModalOpen);
    if (viewModalOpen) {
      setSelectedUser(null);
      setShowNoteInput(false);
      setNoteText('');
    }
  };

  const handleAddNoteClick = () => {
    setShowNoteInput(true);
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      toast.error('Please enter a note.');
      return;
    }

    setIsSavingNote(true);
    try {
      const response = await addUserNote({
        user_id: selectedUser.id,
        email: selectedUser.email,
        note: noteText,
      }).unwrap();
      console.log('Response => ', response);

      toast.success('Note saved successfully.');

      // Update the data with the new note
      let markers = [...data];
      let index = markers.findIndex((el) => el.id === selectedUser.id);
      if (index > -1) {
        // Add the new note to the user_notes array
        const newNote = response?.note || { id: Date.now(), note: noteText, isCompleted: false };
        const existingNotes = markers[index].user_notes || [];

        // Create a deep copy to avoid mutating read-only objects
        markers[index] = {
          ...markers[index],
          user_notes: [newNote, ...existingNotes],
        };
        setData(markers);
        setSelectedUser(markers[index]);
      }

      setNoteText('');
      setShowNoteInput(false);
    } catch (err) {
      console.log(err);
      toast.error('Error saving note.');
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleMarkNoteComplete = async (noteId) => {
    setMarkingCompleteId(noteId);
    try {
      await markUserNoteComplete(noteId).unwrap();
      toast.success('Note marked as complete.');

      // Update the data by marking the note as completed
      let markers = [...data];
      let index = markers.findIndex((el) => el.id === selectedUser.id);
      if (index > -1 && markers[index].user_notes) {
        // Create a deep copy to avoid mutating read-only objects
        markers[index] = {
          ...markers[index],
          user_notes: markers[index].user_notes.map((note) =>
            note.id === noteId ? { ...note, isCompleted: true } : { ...note }
          ),
        };
        setData(markers);
        setSelectedUser(markers[index]);
      }
    } catch (err) {
      console.log(err);
      toast.error('Error marking note as complete.');
    } finally {
      setMarkingCompleteId(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget || !selectedUser) return;
    setIsCancelling(true);
    try {
      await cancelUserSubscription({
        userId: selectedUser.id,
        userSubscriptionId: cancelTarget.id,
        note: cancelNote || undefined,
      }).unwrap();
      toast.success('Subscription cancelled.');
      setCancelTarget(null);
      setCancelNote('');
      await refetchSubs();
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.error || 'Failed to cancel subscription.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleConfirmPause = async () => {
    if (!pauseTarget || !selectedUser) return;
    // Compute the resume date based on the sub's billing cadence (duration=6 → 90d, else 30d)
    const cadenceDays = Number(pauseTarget.duration) === 6 ? 90 : 30;
    let resumeDate;
    if (pauseMode === 'SKIP_1') {
      const d = new Date();
      d.setDate(d.getDate() + cadenceDays);
      resumeDate = d.toISOString().slice(0, 10);
    } else if (pauseMode === 'SKIP_2') {
      const d = new Date();
      d.setDate(d.getDate() + cadenceDays * 2);
      resumeDate = d.toISOString().slice(0, 10);
    } else {
      if (!pauseUntilDate) {
        toast.error('Pick a resume date.');
        return;
      }
      resumeDate = pauseUntilDate;
    }
    setIsPausing(true);
    try {
      await pauseUserSubscription({
        userId: selectedUser.id,
        userSubscriptionId: pauseTarget.id,
        mode: 'UNTIL_DATE',
        resumeDate,
        note: pauseNote || undefined,
      }).unwrap();
      toast.success('Subscription paused.');
      setPauseTarget(null);
      setPauseMode('SKIP_1');
      setPauseUntilDate('');
      setPauseNote('');
      await refetchSubs();
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.error || 'Failed to pause subscription.');
    } finally {
      setIsPausing(false);
    }
  };

  const handleConfirmResume = async () => {
    if (!resumeTarget || !selectedUser) return;
    if (resumeMode === 'ON_DATE' && !resumeOnDate) {
      toast.error('Pick a resume date.');
      return;
    }
    setIsResuming(true);
    try {
      await resumeUserSubscription({
        userId: selectedUser.id,
        userSubscriptionId: resumeTarget.id,
        mode: resumeMode,
        resumeDate: resumeMode === 'ON_DATE' ? resumeOnDate : undefined,
      }).unwrap();
      toast.success(resumeMode === 'IMMEDIATE' ? 'Subscription resumed.' : 'Resume scheduled.');
      setResumeTarget(null);
      setResumeMode('IMMEDIATE');
      setResumeOnDate('');
      await refetchSubs();
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.error || 'Failed to resume subscription.');
    } finally {
      setIsResuming(false);
    }
  };

  const handleEditNoteClick = (note) => {
    console.log('Note => ', note);
    setEditingNoteId(note.id);
    setEditNoteText(note.note);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditNoteText('');
  };

  const handleSaveEdit = async (noteId) => {
    if (!editNoteText.trim()) {
      toast.error('Note cannot be empty.');
      return;
    }

    setIsSavingEdit(true);
    try {
      await editUserNote({ id: noteId, note: editNoteText }).unwrap();
      toast.success('Note updated successfully.');

      // Update the data with the edited note
      let markers = [...data];
      let index = markers.findIndex((el) => el.id === selectedUser.id);
      if (index > -1 && markers[index].user_notes) {
        markers[index] = {
          ...markers[index],
          user_notes: markers[index].user_notes.map((note) =>
            note.id === noteId ? { ...note, note: editNoteText } : { ...note }
          ),
        };
        setData(markers);
        setSelectedUser(markers[index]);
      }

      setEditingNoteId(null);
      setEditNoteText('');
    } catch (err) {
      console.log(err);
      toast.error('Error updating note.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    console.log(selectedRow[0]);
    if (selectedRow[0].admin === 'User') {
      try {
        const response = await deleteUser({ email: selectedRow[0].email });

        console.log(response.data.message);
        if (response.data.message === 'User deleted.') {
          let markers = [...data];
          let index = markers.findIndex((el) => el.id === selectedRow[0].id);
          markers.splice(index, 1);

          setData(markers);

          setToggleSelect(false);

          toast.success('User deleted.');
        } else toast.error('User not deleted.');
      } catch (err) {
        console.log(err.message);
        toast.error("User can't delete.");
      }
    } else if (selectedRow[0].admin === 'Admin') {
      toast.error("User can't delete.");
    }
  };

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (values) => {
    if (values !== '') {
      const index = data.findIndex(
        (el) => el.email === values.userName && editing && el.id !== selectedRow[0].id
      );

      if (index > -1) {
        toast.error('Username already taken.');
      } else {
        const formData = {
          name: values.name,
          email: values.userName,
          password: values.password,
          active: activeChecked ? 1 : 0,
        };

        try {
          if (!editing) {
            // new user
            const res = await addUser(formData).unwrap();
            console.log(res.data);
            let markers = [...data];
            markers.push(res.data);
            setData(markers);

            clears();

            toast.success('User saved.');
          } else {
            // edit user

            formData.id = selectedRow[0].id;

            const res = await updateUser(formData).unwrap();
            console.log(res.data);
            let markers = [...data];
            let index = markers.findIndex((el) => el.id === formData.id);

            markers[index] = res.data;

            setData(markers);

            clears();

            toast.success('User updated.');
          }
        } catch (err) {
          if (err.status === 400) {
            toast.error(err.data?.message);
          } else {
            toast.error('User not saved.');
          }
        }
      }
    } else {
      toast.error('User not saved.');
    }
  };

  const tableColumns = [
    {
      name: 'Id',
      selector: (row) => row.id,
      sortable: true,
      width: '90px',
    },
    {
      name: 'Name',
      selector: (row) => row['name'],
      sortable: true,
      center: false,
    },
    {
      name: 'Username',
      selector: (row) => `${row.email}`,
      sortable: true,
      center: true,
    },
    {
      name: 'Stripe email',
      selector: (row) => row.stripeEmail || '—',
      sortable: true,
      center: true,
    },
    {
      name: 'Type',
      selector: (row) => `${row.admin}`,
      sortable: true,
      center: true,
    },
    {
      name: 'Active',
      selector: (row) => `${row.active}`,
      sortable: true,
      center: true,
    },
    {
      name: 'Incomplete Notes',
      cell: (row) => {
        const incompleteNotes = row?.user_notes?.filter((note) => !note.isCompleted) || [];
        if (incompleteNotes.length === 0) return '-';
        return (
          <ol className="!t-flex !t-flex-col !t-list-disc">
            {incompleteNotes.map((note) => (
              <li key={note.id}>{note.note}</li>
            ))}
          </ol>
        );
      },
      sortable: false,
      center: true,
      wrap: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <Button color="info" size="sm" onClick={() => handleViewUser(row)}>
          View
        </Button>
      ),
      center: true,
      ignoreRowClick: true,
    },
  ];

  return (
    <Fragment>
      <Breadcrumbs mainTitle="User Management" parent="User List" title="Users" subParent="" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <h4 className="t-mb-4 t-font-semibold t-text-lg">Users List</h4>
                <>
                  {selectedRow.length !== 0 && (
                    <>
                      <div className={'float-end w-100 d-flex bg-light-info'}>
                        <div className={`d-flex align-items-center justify-content-between p-2`}>
                          <Btn attrBtn={{ color: 'primary', onClick: () => handleEdit() }}>Update</Btn>
                        </div>

                        <div className={`d-flex align-items-center justify-content-between p-2`}>
                          <Btn attrBtn={{ color: 'danger', onClick: () => toggleModal() }}>Delete</Btn>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Search Bar */}
                  <div className="mb-3">
                    <Input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="form-control"
                    />
                  </div>

                  <DataTable
                    columns={tableColumns}
                    data={data}
                    striped={true}
                    center={true}
                    progressPending={isLoading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={page}
                    paginationPerPage={limit}
                    onChangePage={(newPage) => setPage(newPage)}
                    onChangeRowsPerPage={(newLimit) => {
                      setLimit(newLimit);
                      setPage(1);
                    }}
                    selectableRows
                    selectableRowsSingle
                    selectableRowsHighlight
                    onSelectedRowsChange={handleRowSelected}
                    clearSelectedRows={toggleSelect}
                  />
                </>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ConfirmationModal
        message={'Are you sure to delete the user?'}
        isOpen={modalOpen}
        toggle={toggleModal}
        onConfirm={handleConfirmAction}
      />

      <Modal isOpen={viewModalOpen} toggle={toggleViewModal} size="lg">
        <ModalHeader toggle={toggleViewModal} className="t-text-lg t-font-semibold">
          User Details
        </ModalHeader>
        <ModalBody>
          {selectedUser && (
            <>
              <div className="mb-3">
                <Label className="form-label">User ID</Label>
                <Input type="text" value={selectedUser.id} readOnly disabled />
              </div>
              <div className="mb-3">
                <Label className="form-label">Name</Label>
                <Input type="text" value={selectedUser.name} readOnly disabled />
              </div>
              <div className="mb-3">
                <Label className="form-label">Email</Label>
                <Input type="text" value={selectedUser.email} readOnly disabled />
              </div>
              <div className="mb-3">
                <Label className="form-label">Stripe email</Label>
                <Input type="text" value={selectedUser.stripeEmail || '—'} readOnly disabled />
              </div>

              {/* Subscriptions */}
              <div className="mb-3">
                <Label className="t-text-lg t-font-semibold">Subscriptions</Label>
                {subsFetching ? (
                  <div className="t-text-sm t-text-gray-500">Loading subscriptions…</div>
                ) : subscriptions.length === 0 ? (
                  <div className="t-text-sm t-text-gray-500">No subscriptions on file.</div>
                ) : (
                  <ul className="list-group t-flex t-flex-col !t-list-none !t-pl-0">
                    {subscriptions.map((sub) => {
                      const isActive = sub.status === 'active' || sub.status === 'paused';
                      const pendingUpgrade = hasPendingUpgrade(sub, pendingUpgradeOwnerIds);
                      const pendingChangeDate = pendingUpgrade
                        ? formatPendingChangeDate(sub.pendingPlanChange.effectiveAt)
                        : null;
                      const pendingChangeSummary = formatPendingChangeSummary(sub, pendingChangeDate);
                      return (
                        <li key={sub.id} className="list-group-item t-flex t-flex-col t-gap-2 t-mb-4">
                          <div className="d-flex justify-content-between align-items-start t-gap-2">
                            <div className="t-flex t-flex-col t-gap-0.5 t-min-w-0">
                            <div className="t-text-sm t-flex t-flex-wrap t-items-center t-gap-1.5">
                              <span className="t-font-semibold">#{sub.id}</span>
                              <span className="t-text-gray-600">
                                {sub.product_type || 'riise_formula'}
                              </span>
                              {pendingUpgrade ? (
                                <span className="t-text-xs t-px-2 t-py-0.5 t-rounded-full t-font-semibold t-bg-indigo-50 t-text-indigo-800 t-border t-border-indigo-100">
                                  Upgrade scheduled
                                </span>
                              ) : (
                                <span
                                  className={`t-text-xs t-px-2 t-py-0.5 t-rounded-full t-font-semibold ${
                                    sub.status === 'active'
                                      ? 't-bg-green-100 t-text-green-700'
                                      : sub.status === 'paused'
                                        ? 't-bg-amber-100 t-text-amber-700'
                                        : sub.status === 'cancelled'
                                          ? 't-bg-red-100 t-text-red-700'
                                          : 't-bg-gray-100 t-text-gray-700'
                                  }`}
                                >
                                  {sub.status}
                                </span>
                              )}
                            </div>
                            <div className="t-text-xs t-text-gray-500">
                              duration: {sub.duration ?? '—'} · stripe: {sub.payment_subId || '—'}
                              {sub.pausedUntil ? ` · paused until ${new Date(sub.pausedUntil).toLocaleDateString()}` : ''}
                            </div>
                          </div>
                            <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end t-shrink-0">
                            {canAdminChangePlan(sub) && (
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => setChangePlanTarget(sub)}
                                title="Change billing cadence (monthly ↔ every 3 months)"
                              >
                                Change plan
                              </Button>
                            )}
                            {sub.status === 'active' && (
                              <Button
                                color="warning"
                                size="sm"
                                onClick={() => setPauseTarget(sub)}
                                disabled={!sub.payment_subId}
                                title={!sub.payment_subId ? 'No Stripe id; cannot pause' : 'Pause subscription'}
                              >
                                Pause
                              </Button>
                            )}
                            {sub.status === 'paused' && (
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => setResumeTarget(sub)}
                                disabled={!sub.payment_subId}
                                title={!sub.payment_subId ? 'No Stripe id; cannot resume' : 'Resume subscription'}
                              >
                                Resume
                              </Button>
                            )}
                            {isActive && (
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => setCancelTarget(sub)}
                                disabled={!sub.payment_subId}
                                title={!sub.payment_subId ? 'No Stripe id; cannot cancel' : 'Cancel subscription'}
                              >
                                Cancel
                              </Button>
                            )}
                            {sub.payment_subId && (
                              <a
                                href={`https://dashboard.stripe.com/subscriptions/${sub.payment_subId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Open ${sub.payment_subId} in Stripe`}
                                className="text-secondary d-inline-flex align-items-center"
                              >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 3h7v7M10 14L21 3M21 14v7H3V3h7" />
                                </svg>
                              </a>
                            )}
                          </div>
                          </div>
                          {pendingChangeSummary && (
                            <div className="t-flex t-w-full t-items-start t-gap-2 t-rounded-lg t-bg-indigo-50 t-border t-border-indigo-100 t-px-3 t-py-2 t-text-xs t-font-medium t-text-indigo-800 sm:t-text-sm">
                              <span aria-hidden="true" className="t-text-base t-leading-none t-shrink-0">
                                ↻
                              </span>
                              <span>{pendingChangeSummary}</span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Display all notes */}
              {selectedUser.user_notes && selectedUser.user_notes.length > 0 && (
                <div className="mb-3">
                  <Label className="t-text-lg t-font-semibold">Notes</Label>
                  <ul className="t-flex t-flex-col t-gap-2 !t-list-disc">
                    {selectedUser.user_notes.map((note) => (
                      <li
                        key={note.id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {editingNoteId === note.id ? (
                          <>
                            <Input
                              type="text"
                              value={editNoteText}
                              onChange={(e) => setEditNoteText(e.target.value)}
                              className="flex-grow-1 me-2"
                            />
                            <div className="d-flex gap-1">
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => handleSaveEdit(note.id)}
                                disabled={isSavingEdit}
                                style={{ opacity: isSavingEdit ? 0.5 : 1 }}
                                title="Save"
                              >
                                <i className="fa fa-check"></i>
                              </Button>
                              <Button
                                color="secondary"
                                size="sm"
                                onClick={handleCancelEdit}
                                disabled={isSavingEdit}
                                title="Cancel"
                              >
                                <i className="fa fa-times"></i>
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <span>{note.note}</span>
                            {note.isCompleted ? (
                              <span className="t-text-green-500 t-text-base">Completed</span>
                            ) : (
                              <div className="d-flex gap-1">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleEditNoteClick(note)}
                                  title="Edit"
                                >
                                  <i className="fa fa-edit"></i>
                                </Button>
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => handleMarkNoteComplete(note.id)}
                                  disabled={markingCompleteId === note.id}
                                  style={{ opacity: markingCompleteId === note.id ? 0.5 : 1 }}
                                  title="Mark as Complete"
                                >
                                  <i className="fa fa-check-circle"></i>
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!showNoteInput ? (
                <Button color="primary" onClick={handleAddNoteClick}>
                  Add Note
                </Button>
              ) : (
                <>
                  <div className="mb-3">
                    <Label className="form-label">Note</Label>
                    <Input
                      type="textarea"
                      rows="4"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Enter your note here..."
                    />
                  </div>
                  <Button
                    color="success"
                    onClick={handleSaveNote}
                    disabled={isSavingNote}
                    style={{ opacity: isSavingNote ? 0.5 : 1 }}
                  >
                    {isSavingNote ? 'Saving...' : 'Save Note'}
                  </Button>
                </>
              )}
            </>
          )}
        </ModalBody>
      </Modal>

      <AdminChangePlanModal
        open={Boolean(changePlanTarget)}
        onClose={() => setChangePlanTarget(null)}
        userId={selectedUser?.id}
        userSubscriptionId={changePlanTarget?.id}
        userEmail={selectedUser?.email}
        onSuccess={() => refetchSubs()}
      />

      <Modal isOpen={!!pauseTarget} toggle={() => !isPausing && setPauseTarget(null)}>
        <ModalHeader toggle={() => !isPausing && setPauseTarget(null)}>
          Pause subscription
        </ModalHeader>
        <ModalBody>
          {pauseTarget && (
            <>
              <p className="t-text-sm">
                Pause subscription <strong>#{pauseTarget.id}</strong> ({pauseTarget.product_type || 'riise_formula'}) for{' '}
                <strong>{selectedUser?.email}</strong>?
              </p>
              <p className="t-text-xs t-text-gray-500 t-mt-2">
                Stripe will skip billing until the resume date by extending the trial. Customer can be auto-resumed later.
              </p>
              <div className="mt-3">
                <Label className="form-label">Resume after</Label>
                <div className="d-flex flex-column gap-2">
                  {[
                    { key: 'SKIP_1', label: 'Skip 1 shipment' },
                    { key: 'SKIP_2', label: 'Skip 2 shipments' },
                    { key: 'UNTIL_DATE', label: 'Specific date' },
                  ].map((opt) => (
                    <label key={opt.key} className="d-flex align-items-center gap-2 t-cursor-pointer">
                      <input
                        type="radio"
                        name="pauseMode"
                        value={opt.key}
                        checked={pauseMode === opt.key}
                        onChange={() => setPauseMode(opt.key)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
                {pauseMode === 'UNTIL_DATE' && (
                  <Input
                    type="date"
                    className="mt-2"
                    value={pauseUntilDate}
                    onChange={(e) => setPauseUntilDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                )}
              </div>
              <div className="mt-3">
                <Label className="form-label">Optional note (audit)</Label>
                <Input
                  type="textarea"
                  rows="2"
                  value={pauseNote}
                  onChange={(e) => setPauseNote(e.target.value)}
                  placeholder="e.g. customer asked to pause for travel"
                />
              </div>
              <div className="d-flex gap-2 mt-3 justify-content-end">
                <Button color="light" onClick={() => setPauseTarget(null)} disabled={isPausing}>
                  Back
                </Button>
                <Button color="warning" onClick={handleConfirmPause} disabled={isPausing}>
                  {isPausing ? 'Pausing…' : 'Confirm pause'}
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={!!resumeTarget} toggle={() => !isResuming && setResumeTarget(null)}>
        <ModalHeader toggle={() => !isResuming && setResumeTarget(null)}>
          Resume subscription
        </ModalHeader>
        <ModalBody>
          {resumeTarget && (
            <>
              <p className="t-text-sm">
                Resume subscription <strong>#{resumeTarget.id}</strong> ({resumeTarget.product_type || 'riise_formula'}) for{' '}
                <strong>{selectedUser?.email}</strong>?
              </p>
              <div className="mt-3">
                <Label className="form-label">When</Label>
                <div className="d-flex flex-column gap-2">
                  {[
                    { key: 'IMMEDIATE', label: 'Resume now' },
                    { key: 'ON_DATE', label: 'Resume on specific date' },
                  ].map((opt) => (
                    <label key={opt.key} className="d-flex align-items-center gap-2 t-cursor-pointer">
                      <input
                        type="radio"
                        name="resumeMode"
                        value={opt.key}
                        checked={resumeMode === opt.key}
                        onChange={() => setResumeMode(opt.key)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
                {resumeMode === 'ON_DATE' && (
                  <Input
                    type="date"
                    className="mt-2"
                    value={resumeOnDate}
                    onChange={(e) => setResumeOnDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                )}
              </div>
              <p className="t-text-xs t-text-gray-500 t-mt-2">
                {resumeMode === 'IMMEDIATE'
                  ? 'Stripe trial ends now; the next billing cycle begins on its normal schedule.'
                  : 'Stripe trial extends to the chosen date; sub stays paused until then.'}
              </p>
              <div className="d-flex gap-2 mt-3 justify-content-end">
                <Button color="light" onClick={() => setResumeTarget(null)} disabled={isResuming}>
                  Back
                </Button>
                <Button color="success" onClick={handleConfirmResume} disabled={isResuming}>
                  {isResuming ? 'Resuming…' : 'Confirm'}
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={!!cancelTarget} toggle={() => !isCancelling && setCancelTarget(null)}>
        <ModalHeader toggle={() => !isCancelling && setCancelTarget(null)}>
          Cancel subscription
        </ModalHeader>
        <ModalBody>
          {cancelTarget && (
            <>
              <p className="t-text-sm">
                Cancel subscription <strong>#{cancelTarget.id}</strong> ({cancelTarget.product_type || 'riise_formula'}) for{' '}
                <strong>{selectedUser?.email}</strong>?
              </p>
              <p className="t-text-xs t-text-gray-500 t-mt-2">
                This calls Stripe immediately. Webhook will then flip the DB status and tag the Omnisend contact as cancelled.
              </p>
              <div className="mt-3">
                <Label className="form-label">Optional note (audit)</Label>
                <Input
                  type="textarea"
                  rows="2"
                  value={cancelNote}
                  onChange={(e) => setCancelNote(e.target.value)}
                  placeholder="e.g. customer support request via email"
                />
              </div>
              <div className="d-flex gap-2 mt-3 justify-content-end">
                <Button color="light" onClick={() => setCancelTarget(null)} disabled={isCancelling}>
                  Back
                </Button>
                <Button color="danger" onClick={handleConfirmCancel} disabled={isCancelling}>
                  {isCancelling ? 'Cancelling…' : 'Confirm cancel'}
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <ToastContainer />
    </Fragment>
  );
};

export default Users;

{
  /* <CardHeader>
<H5>New User</H5>
</CardHeader>
<CardBody>
<Form className="needs-validation" noValidate="" onSubmit={handleSubmit(onSubmit)}>
    <Row>
    <Col md="4 mb-3">
        <Label className="form-label" for="name">
        Name
        </Label>
        <input
        className="form-control"
        id="name"
        type="text"
        name="name"
        placeholder="Name"
        {...register('name', { required: true })}
        />
        <span className="d-block txt-danger">{errors.name && 'Name is required'}</span>
    </Col>
    <Col md="4 mb-3">
        <Label for="userName">{Username}</Label>
        <InputGroup>
        <InputGroupText>{'@'}</InputGroupText>
        <input
            className="form-control"
            id="userName"
            name="userName"
            type="text"
            placeholder="Username"
            {...register('userName', { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })}
        />
        <span className="d-block txt-danger w-100">
            {errors.userName && 'User name is required'}
        </span>
        </InputGroup>
    </Col>
    <Col md="4 mb-3">
        {editing ? (
        <div className="form-check">
            <div className="checkbox p-0">
            <Input
                className="form-check-input"
                id="passwordCheck"
                type="checkbox"
                checked={passwordChecked}
                onClick={() => {
                setPasswordChecked(!passwordChecked);
                }}
            />
            <Label className="form-check-label mt-0 mb-0" htmlFor="passwordCheck">
                {'Change password'}
            </Label>
            </div>
        </div>
        ) : (
        <Label className="form-label" for="password">
            Password
        </Label>
        )}
        <input
        className="form-control"
        id="password"
        type="password"
        name="password"
        {...register('password', {
            required: !editing || (editing && passwordChecked),
            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
        })}
        />
        <span className="d-block txt-danger">
        {editing &&
            passwordChecked &&
            errors.password &&
            'Password should contains a lowercase, a uppercase character and a digit and atleast 8 characters.'}
        </span>
    </Col>
    </Row>

    <div>
    <div className="form-check">
        <div className="checkbox p-0">
        <Input
            className="form-check-input"
            id="activeCheck"
            type="checkbox"
            checked={activeChecked}
            onClick={() => {
            setActiveChecked(!activeChecked);
            }}
            {...register('activeCheck')}
        />
        <Label className="form-check-label" htmlFor="activeCheck">
            {'User Active'}
        </Label>
        </div>
    </div>
    </div>
    <Btn attrBtn={{ color: 'primary' }}>{editing ? 'Update user' : 'Save user'}</Btn>
</Form>
</CardBody> */
}
